/**
 * src/components/shared/breadcrumb/BreadcrumbVoice.jsx
 * AC Nautitech — Voice Breadcrumbs
 *
 * Transcript display (collapsible) with edit/save/reprocess controls,
 * and audio playback with speed controls.
 */

import { useRef, useCallback, useState } from 'react';
import { Edit2, Save } from 'lucide-react';

// ── Transcript Section ───────────────────────────────────────────────

export function BreadcrumbTranscript({ transcriptText, editing, editText, setEditText, startEdit, cancelEdit, saveEdit, saving, t }) {
  if (!transcriptText) return null;

  return (
    <details style={{ background: 'var(--bg)', borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)', position: 'relative' }}>
      <summary style={{ padding: '14px 20px', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: 'var(--tm, #8896AB)', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>Full Transcript</span>
      </summary>
      {/* a11y 2026-04-26: Edit button moved OUT of <summary> — interactive
          descendants of <summary> are HTML-invalid and surface as Chrome
          warnings ("Disallowed descendant" / "Interactive element inside
          of a <summary>"). Position absolute keeps the visual layout. */}
      {!editing && (
        <button
          data-testid="bc-edit-text"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); startEdit(); }}
          style={{ position: 'absolute', top: 9, right: 16, padding: '8px 12px', minHeight: 44, borderRadius: 8, fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--stodob)', color: 'var(--tm)', display: 'inline-flex', alignItems: 'center', gap: 4, zIndex: 1 }}>
          <Edit2 size={10} /> Edit
        </button>
      )}
      <div style={{ padding: '0 20px 20px' }}>
        {editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              autoFocus
              disabled={saving}
              data-testid="bc-edit-textarea"
              style={{
                padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--bdr)',
                background: saving ? 'var(--stodob)' : 'var(--glass)',
                color: saving ? 'var(--tm)' : 'var(--t1)',
                fontSize: 13, width: '100%', minHeight: 80,
                resize: 'vertical', fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box',
                cursor: saving ? 'not-allowed' : 'text',
                opacity: saving ? 0.7 : 1,
              }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                data-testid="bc-save-text"
                onClick={() => saveEdit(false)}
                disabled={saving}
                title={t('breadcrumbCard.saveTextOnly')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 16px', minHeight: 44, borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--stodob)', color: 'var(--t2)', opacity: saving ? 0.6 : 1 }}
              >
                <Save size={12} /> {saving ? 'Saving...' : 'Save text'}
              </button>
              <button
                data-testid="bc-save-reprocess"
                onClick={() => saveEdit(true)}
                disabled={saving}
                title={t('breadcrumbCard.saveAndReprocess')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 16px', minHeight: 44, borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--accent)', color: 'var(--card)', opacity: saving ? 0.6 : 1 }}
              >
                <Save size={12} /> {saving ? 'Reprocessing...' : 'Save & reprocess actions'}
              </button>
              <button
                data-testid="bc-cancel-edit"
                onClick={cancelEdit}
                disabled={saving}
                style={{ padding: '8px 16px', minHeight: 44, borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'var(--glass)', color: 'var(--tm)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--t2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {transcriptText}
          </div>
        )}
      </div>
    </details>
  );
}

// ── Audio Playback ───────────────────────────────────────────────────

export function BreadcrumbAudioPlayer({ audioUrl }) {
  const audioRef = useRef(null);
  const [playbackRate, setPlaybackRate] = useState(1);

  const setRate = useCallback((rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, []);

  if (!audioUrl) return null;

  return (
    <div>
      <audio ref={audioRef} controls src={audioUrl} className="w-full rounded-lg mb-1.5"
        onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = playbackRate; }} />
      <div className="inline-flex rounded-lg border border-bdr overflow-hidden">
        {[1, 1.5, 2].map(rate => (
          <button key={rate} onClick={(e) => { e.stopPropagation(); setRate(rate); }}
            className={`px-2.5 py-1 text-[11px] font-semibold border-none cursor-pointer ${
              playbackRate === rate ? 'bg-accent text-[var(--on-accent)]' : 'bg-stodob text-tm'
            }`}>
            {rate}x
          </button>
        ))}
      </div>
    </div>
  );
}
